import { Link } from "react-router-dom";
import type { News, Plot } from "@/types/content";
import { formatDateCn, getWork, getAllNews, getAllPlots } from "@/lib/content";
import ArticleContent from "./ArticleContent";
import ShareButton from "./ShareButton";
import Breadcrumb from "@/components/base/Breadcrumb";

interface ArticleDetailProps {
  kind: "news" | "plot";
  item: News | Plot;
}

export default function ArticleDetail({ kind, item }: ArticleDetailProps) {
  const isNews = kind === "news";
  const news = isNews ? (item as News) : null;
  const plot = !isNews ? (item as Plot) : null;

  const relatedWork = getWork(isNews ? news!.relatedWorkId : plot!.relatedWorkId);

  const recommendations = isNews
    ? getAllNews()
        .filter((n) => n.id !== item.id)
        .slice(0, 4)
    : getAllPlots()
        .filter((p) => p.id !== item.id)
        .slice(0, 3);

  const rootLabel = isNews ? "影视资讯" : "剧情解析";
  const rootPath = isNews ? "/category/news" : "/category/plot";

  return (
    <div className="hp-fade-up">
      <section className="relative w-full">
        <div className="absolute inset-0 h-[360px] md:h-[420px] overflow-hidden">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} 配图`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-foreground-950/85 to-foreground-950/80" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 pt-24 md:pt-28 pb-4">
          <div className="mb-4 [&_a]:text-background-300 [&_a]:hover:text-accent-400 [&_span]:text-background-200">
            <Breadcrumb items={[{ label: rootLabel, to: rootPath }, { label: item.title }]} />
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="bg-accent-500 text-background-50 text-xs px-2.5 py-1 rounded-md">
              {isNews ? news!.type : "剧情解析"}
            </span>
            <span className="text-xs text-background-200 flex items-center gap-1">
              <i className="ri-calendar-line" />
              {formatDateCn(item.date)}
            </span>
            {isNews && (
              <span className="text-xs text-background-200 flex items-center gap-1">
                <i className="ri-user-line" />
                {news!.source}
              </span>
            )}
          </div>
          <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50 tracking-tight max-w-3xl leading-tight">
            {item.title}
          </h1>
          {plot && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {plot.tags.map((tag) => (
                <span key={tag} className="bg-background-50/15 text-background-100 text-xs px-2.5 py-1 rounded-md">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pt-6 md:pt-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <article className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <p className="text-sm md:text-base text-foreground-600 leading-loose mb-6 pb-6 border-b border-background-200">
                {item.summary}
              </p>
              <ArticleContent content={item.content} />
              <div className="mt-8 pt-6 border-t border-background-200 flex items-center justify-between flex-wrap gap-4">
                <span className="text-sm text-foreground-500">
                  本文由黑皮辣妹编辑组整理 · 仅供演示
                </span>
                <ShareButton />
              </div>
            </div>
          </article>

          <aside className="space-y-8">
            {relatedWork && (
              <div>
                <h2 className="font-heading font-black text-xl text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-film-line text-primary-500" />
                  相关作品
                </h2>
                <Link
                  to={`/detail/${relatedWork.id}`}
                  className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                >
                  <span className="w-20 shrink-0 aspect-[3/4] rounded-md overflow-hidden bg-background-200">
                    <img
                      src={relatedWork.cover}
                      alt={relatedWork.title}
                      title={`${relatedWork.title} 影视作品海报`}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-heading font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {relatedWork.title}
                    </span>
                    <span className="block text-xs text-foreground-500 mt-1">
                      {relatedWork.year} · {relatedWork.genre.join(" / ")}
                    </span>
                    <span className="inline-flex items-center gap-1 text-accent-600 text-sm font-label font-semibold mt-2">
                      <i className="ri-star-fill text-xs" />
                      {relatedWork.rating.toFixed(1)}
                    </span>
                  </span>
                </Link>
              </div>
            )}

            <div>
              <h2 className="font-heading font-black text-xl text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-accent-500" />
                相关推荐
              </h2>
              <div className="space-y-2">
                {recommendations.map((rec) => (
                  <Link
                    key={rec.id}
                    to={`/detail/${rec.id}`}
                    className="group flex items-center gap-2 rounded-md bg-secondary-50 border border-secondary-200 px-3.5 py-3 hover:border-primary-300 transition-colors"
                  >
                    <i className={isNews ? "ri-newspaper-line text-secondary-500" : "ri-article-line text-secondary-500"} />
                    <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rec.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}